<i18n lang="yaml">
en:
  edit: "Edit"
  fields:
    personal:
      message: "Message"
  tags:
    personal:
      specialties:
        rent: "Leasing"
        sale: "Brokerage"
        investment: "Investment"
ja:
  edit: "編集する"
  placeholders:
    personal:
      message: |
        （例）私は、〇〇不動産で10年以上の経験を持つエージェントです。お客様のニーズに最適な物件をご提案し、安心して取引を進めていただけるよう全力を尽くします。趣味は旅行と読書で、休日は家族と過ごすことが多いです。どうぞお気軽にご相談ください。{br}
        趣味は旅行と読書で、休日は家族と過ごすことが多いです。
      motto: "（例）努力は裏切らない"
      freeTime: "（例）家族と一緒にアウトドアを楽しむ"
      hobby: "（例）登山・ハイキング"
      skill: "（例）幅広いコミュニケーション力"
      birthplace: "（例）東京都世田谷区"
  fields:
    personal:
      message: "エージェントからのメッセージ"
      specialties: "専門分野"
      certificates: "保有資格"
      languages: "対応言語"
      areas: "対応エリア"
      motto: "モットー/好きな言葉"
      freeTime: "休日の過ごし方"
      hobby: "趣味"
      skill: "特技"
      birthplace: "出身地"
  tags:
    personal:
      specialties:
        rent: "賃貸"
        sale: "売買"
        investment: "投資"
</i18n>
<script setup lang="ts">
import { ref } from "vue";
import { storeToRefs } from "pinia";

import { useI18n } from "vue-i18n";
import { use as useSession } from "@/stores/session";
import { useAPI } from "@/composables/api";

import type { UserProfile } from "@/types/profiles.d";

import UIEditable from "@/components/ui/Editable.vue";
import UITagList from "@/components/ui/TagList.vue";

import ItemTags from "./ProfileTags.vue";
import ItemCertificates from "./ProfileCertificates.vue";
import ItemLanguages from "./ProfileLanguages.vue";
import ItemAreas from "./ProfileAreas.vue";

// inject
const { t } = useI18n();
const session = useSession();
const api = useAPI();
const { agent, active } = storeToRefs(session);

const data = ref({ personal: { ...agent.value?.personal } });

type Value = string | string[] | null;
interface Nested {
  [key: string]: Nested | Value;
}
function getNested(obj: Nested | Record<string, Value>, path: string): Value {
  const value =
    path
      .split(".")
      .reduce(
        (obj, key) => obj?.[key] as Nested | Record<string, Value>,
        obj
      ) ?? null;
  if (typeof value == "object" && !Array.isArray(value) && value != null)
    throw new Error("Not a value");
  return value;
}
function setNested(obj: Nested, path: string, value: Value) {
  const keys = path.split("."),
    key = keys.pop() as string;
  obj = keys.reduce((obj, key) => obj?.[key] as Nested, obj);
  if (obj) obj[key] = value;
}

async function updateProfile(update: Record<string, Value>) {
  if (agent.value) {
    await api.call("updateProfile", update);
    for (const [path, value] of Object.entries(update)) {
      setNested(agent.value as unknown as Nested, path, value);
    }
  }
}

async function onCancel(set: (v: boolean) => void, paths: string[]) {
  if (agent.value) {
    for (const path of paths) {
      // reset to original value
      setNested(
        data.value,
        path,
        getNested(agent.value as unknown as Nested, path)
      );
    }
  }
  set(false);
}
async function onSave(set: (v: boolean) => void, paths: string[]) {
  const update = paths.reduce((update, path) => {
    update[path] = getNested(data.value, path);
    return update;
  }, {} as Record<string, Value>);
  await updateProfile(update);
  set(false);
}

const groups = [
  ["personal.message"],
  [
    "personal.specialties",
    "personal.certificates",
    "personal.languages",
    "personal.areas",
  ],
  [
    "personal.motto",
    "personal.freeTime",
    "personal.hobby",
    "personal.skill",
    "personal.birthplace",
  ],
];

const options = {
  specialties: ["rent", "sale", "investment"].map((id) => ({
    id,
    label: t("tags.personal.specialties." + id),
  })),
};
</script>
<style scoped>
.profile {
  width: 100%;
}
@media (max-width: 768px) {
  .profile {
    padding: 0 12px;
  }
}
.btn.primary {
  padding: 0 24px;
}
hr {
  display: block;
  height: 1px;
  border: 0;
  border-top: 1px solid var(--cl-black-10);
  margin: 24px 0;
}
textarea {
  height: 110px;
  padding: 16px;
}
.head {
  margin-bottom: 8px;
}
.sample {
  color: var(--cl-black-40);
}
</style>
<style>
.i-box {
  width: 100%;
  font-size: 16px;
  padding: 12px;
}
.i-box.tags {
  height: unset;
  padding: 6px;
}
.field {
  position: relative;
}
.field p {
  margin-top: 8px;
}
.field + .field {
  margin-top: 16px;
}
.field .i-box {
  margin-top: 8px;
}
.field [type="text"] + .tags {
  position: absolute;
  width: 100%;
  border-radius: 0;
  top: calc(100% - 1px);
  z-index: 1;
}
.field .tags {
  flex-wrap: wrap;
}
.field .tags.ro .tag {
  color: var(--cl-black-80);
  padding: 8px 10px;
  border-radius: 3px;
  margin-right: 8px;
  line-height: 14px;
  user-select: none;
  background: #f3f3f3;
}
.field .tags .tag svg {
  color: var(--cl-black-60);
  margin-left: 4px;
}
.field .tags.ro .tag {
  margin-top: 8px;
}
</style>
<template>
  <section class="profile u-flex t16" v-if="agent">
    <UIEditable v-slot="{ editing, set }">
      <div class="head u-row">
        <h2 v-t="'fields.personal.message'" class="u-flex t20 w6" />
        <template v-if="editing">
          <button
            type="button"
            class="btn cancel cl-orange w6"
            v-t="'cancel'"
            @click="onCancel(set, groups[0])"
          />
          <button
            type="button"
            class="btn primary w6"
            v-t="'save'"
            @click="onSave(set, groups[0])"
          />
        </template>
        <button
          type="button"
          class="btn cl-orange w6"
          @click="set(true)"
          v-else-if="active"
        >
          <FontAwesomeIcon class="icon" :icon="['fas', 'pencil']" />
          <span v-t="'edit'" />
        </button>
      </div>
      <textarea
        class="i-box"
        :placeholder="t('placeholders.personal.message')"
        v-model="data.personal.message"
        v-if="editing"
      />
      <template v-else>
        <p v-if="agent.personal.message">{{ agent.personal.message }}</p>
        <i18n-t
          tag="p"
          class="sample"
          keypath="placeholders.personal.message"
          v-else-if="active"
        >
          <template #br><br /></template>
        </i18n-t>
      </template>
    </UIEditable>

    <hr />

    <UIEditable v-slot="{ editing, set }">
      <div class="head u-row">
        <h2 class="u-flex" />
        <template v-if="editing">
          <button
            type="button"
            class="btn cancel cl-orange w6"
            v-t="'cancel'"
            @click="onCancel(set, groups[1])"
          />
          <button
            type="button"
            class="btn primary w6"
            v-t="'save'"
            @click="onSave(set, groups[1])"
          />
        </template>
        <button
          type="button"
          class="btn cl-orange w6"
          @click="set(true)"
          v-else-if="active"
        >
          <FontAwesomeIcon class="icon" :icon="['fas', 'pencil']" />
          <span v-t="'edit'" />
        </button>
      </div>

      <div class="field">
        <h3 class="w6 t16" v-t="'fields.personal.specialties'" />
        <UITagList
          :tags="options.specialties"
          :value="agent.personal.specialties"
          v-if="editing"
          @change="data.personal.specialties = $event"
        />
        <ItemTags
          :tags="
            agent.personal.specialties?.map((tag) =>
              t('tags.personal.specialties.' + tag)
            )
          "
          v-else-if="agent.personal.specialties || active"
        />
      </div>

      <ItemCertificates :editing="editing" :data="data" />
      <ItemLanguages :editing="editing" :data="data" />
      <ItemAreas :editing="editing" :data="data" />
    </UIEditable>

    <hr />

    <UIEditable v-slot="{ editing, set }">
      <div class="head u-row">
        <h2 class="u-flex" />
        <template v-if="editing">
          <button
            type="button"
            class="btn cancel cl-orange w6"
            v-t="'cancel'"
            @click="onCancel(set, groups[2])"
          />
          <button
            type="button"
            class="btn primary w6"
            v-t="'save'"
            @click="onSave(set, groups[2])"
          />
        </template>
        <button
          type="button"
          class="btn cl-orange w6"
          @click="set(true)"
          v-else-if="active"
        >
          <FontAwesomeIcon class="icon" :icon="['fas', 'pencil']" />
          <span v-t="'edit'" />
        </button>
      </div>
      <div class="field" v-for="field of groups[2]" :key="field">
        <h3 class="w6 t16" v-t="'fields.' + field" />
        <input
          type="text"
          class="i-box"
          :placeholder="t('placeholders.' + field)"
          v-model="data.personal[field.split('.')[1] as keyof UserProfile['personal']]"
          v-if="editing"
        />
        <template v-else>
          <p v-if="getNested(agent as unknown as Nested, field)">
            {{ getNested(agent as unknown as Nested, field) }}
          </p>
          <i18n-t
            tag="p"
            class="sample"
            :keypath="'placeholders.' + field"
            v-else-if="active"
          >
            <template #br><br /></template>
          </i18n-t>
        </template>
      </div>
    </UIEditable>
  </section>
</template>
